import axios, { HttpStatusCode } from 'axios';
import { isNil, isObject, isString } from 'lodash';
import type { ApiErrorResponse } from 'shared/api';
import { isTypeOf } from 'shared/type-utils';
import {
  IMAGE_UPLOAD_FAILED_MESSAGE,
  MAX_IMAGE_UPLOAD_RETRIES,
  MISSING_PRESIGNED_URLS_MESSAGE,
  RETRY_BACKOFF_FACTOR,
  RETRY_BASE_DELAY_MS,
  retryableHttpStatusCodes,
} from '../constants';
import { UploadErrorAction } from '../enums';

// Thrown when the backend returns fewer presigned URLs than filenames requested.
export class MissingPresignedUrlsError extends Error {
  constructor() {
    super(MISSING_PRESIGNED_URLS_MESSAGE);
    this.name = 'MissingPresignedUrlsError';
  }
}

const isApiErrorResponse = (error: unknown): error is ApiErrorResponse =>
  isObject(error) && isTypeOf<ApiErrorResponse>(error, 'meta');

/**
 * Decides how the image set uploader reacts to a failed request.
 *
 * Network failures never use up retries. They wait for the connection instead.
 *
 * @param error - The rejection from the failed request.
 * @param retryCount - Retries already made for this request.
 * @param isSignedUrlRequest - Whether the request was the PUT to the storage signed URL.
 * @returns The action the uploader takes next.
 */
export const getUploadErrorAction = (
  error: unknown,
  retryCount: number,
  isSignedUrlRequest: boolean,
): UploadErrorAction => {
  if (axios.isCancel(error)) {
    return UploadErrorAction.Ignore;
  }

  if (error instanceof MissingPresignedUrlsError) {
    return retryCount >= MAX_IMAGE_UPLOAD_RETRIES
      ? UploadErrorAction.Fail
      : UploadErrorAction.Retry;
  }

  if (!axios.isAxiosError(error)) {
    return UploadErrorAction.Fail;
  }

  const status = error.response?.status;

  if (isNil(status)) {
    return UploadErrorAction.WaitForNetwork;
  }

  if (retryCount >= MAX_IMAGE_UPLOAD_RETRIES) {
    return UploadErrorAction.Fail;
  }

  // A 403 from storage usually means the signed URL expired.
  if (status === HttpStatusCode.Forbidden && isSignedUrlRequest) {
    return UploadErrorAction.RefreshUrlAndRetry;
  }

  if (
    status >= HttpStatusCode.InternalServerError ||
    retryableHttpStatusCodes.includes(status)
  ) {
    return UploadErrorAction.Retry;
  }

  return UploadErrorAction.Fail;
};

export const getRetryDelay = (retryIndex: number): number =>
  RETRY_BASE_DELAY_MS * RETRY_BACKOFF_FACTOR ** retryIndex;

export const getUploadErrorMessage = (error: unknown): string => {
  if (error instanceof MissingPresignedUrlsError) {
    return error.message;
  }

  const responseData = axios.isAxiosError(error) ? error.response?.data : error;
  const message = isApiErrorResponse(responseData)
    ? responseData.meta?.message
    : undefined;

  return isString(message) && message ? message : IMAGE_UPLOAD_FAILED_MESSAGE;
};
